import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Cart, CartItem } from '../types';
import { loadSettings } from '@/features/settings/hooks/use-settings';
import { DEFAULT_PRICING_CONFIG, resolvePricingConfig } from '@/features/settings/pricing-config';

let pricingConfig: {
  vatRate: number;
  baseDeliveryFee: number;
  freeDeliveryThreshold: number;
} = { ...DEFAULT_PRICING_CONFIG };

let pricingLoaded = false;

async function loadPricingConfig(): Promise<void> {
  if (pricingLoaded) return;

  try {
    const settings = await loadSettings();
    pricingConfig = resolvePricingConfig(settings);
    pricingLoaded = true;
    console.log('[CartStore] Pricing loaded from Supabase settings:', pricingConfig);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[CartStore] Failed to load pricing config from Supabase settings:', errorMessage);

    if (error instanceof Error && error.stack) {
      console.error('[CartStore] Stack trace:', error.stack);
    }
  }
}

function calculateTotals(items: CartItem[]) {
  const fee = pricingConfig.baseDeliveryFee;
  const threshold = pricingConfig.freeDeliveryThreshold;

  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const tax = subtotal * pricingConfig.vatRate;
  const deliveryFeeAmount = subtotal >= threshold ? 0 : fee;
  const total = subtotal + tax + deliveryFeeAmount;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return { subtotal, tax, deliveryFee: deliveryFeeAmount, total, itemCount };
}

interface CartState extends Cart {
  isOpen: boolean;
  isSyncing: boolean;
  lastError?: string;
  pricingLoaded: boolean;
  lastAddedItem?: {
    id: string;
    productName: string;
    quantity: number;
    variantLabel?: string;
    addedAt: string;
  };
  addItem: (item: Omit<CartItem, 'id' | 'addedAt'>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  clearAddFeedback: () => void;
  setBranchId: (branchId: string | undefined) => void;
  setCustomerId: (customerId: string | undefined) => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  setSyncing: (isSyncing: boolean) => void;
  setLastSynced: (timestamp: string) => void;
  setError: (error: string | undefined) => void;
  loadPricing: () => Promise<void>;
  getItemCount: () => number;
  getSubtotal: () => number;
  getTotal: () => number;
  getItemByProduct: (productId: string, variantId?: string) => CartItem | undefined;
}

const initialState: Omit<CartState,
  'addItem' | 'removeItem' | 'updateQuantity' | 'clearCart' |
  'clearAddFeedback' |
  'setBranchId' | 'setCustomerId' | 'openCart' | 'closeCart' | 'toggleCart' |
  'setSyncing' | 'setLastSynced' | 'setError' | 'getItemCount' | 'getSubtotal' |
  'getTotal' | 'getItemByProduct' | 'loadPricing'
> = {
  items: [],
  subtotal: 0,
  tax: 0,
  deliveryFee: 0,
  total: 0,
  itemCount: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isOpen: false,
  isSyncing: false,
  pricingLoaded: false,
};

function generateItemId(productId: string, variantId?: string): string {
  return variantId ? `${productId}:${variantId}` : productId;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      ...initialState,

      addItem: (itemData) => {
        const hasValidPrice =
          Number.isFinite(itemData.unitPrice) && itemData.unitPrice > 0;
        if (!hasValidPrice) {
          set({
            lastError: `Price unavailable for "${itemData.product.name}". Please contact support.`,
          });
          return;
        }

        const { items } = get();
        const itemId = generateItemId(itemData.productId, itemData.variantId);
        const existingItemIndex = items.findIndex(item => item.id === itemId);
        const feedback = {
          id: itemId,
          productName: itemData.product.name,
          quantity: itemData.quantity,
          variantLabel: itemData.variant?.volume,
          addedAt: new Date().toISOString(),
        };

        let newItems: CartItem[];

        if (existingItemIndex >= 0) {
          newItems = items.map((item, index) =>
            index === existingItemIndex
              ? { ...item, quantity: item.quantity + itemData.quantity }
              : item
          );
        } else {
          const newItem: CartItem = {
            ...itemData,
            id: itemId,
            addedAt: new Date().toISOString(),
          };
          newItems = [...items, newItem];
        }

        const totals = calculateTotals(newItems);

        set({
          items: newItems,
          ...totals,
          lastAddedItem: feedback,
          lastError: undefined,
          updatedAt: new Date().toISOString(),
        });
      },

      removeItem: (itemId) => {
        const { items } = get();
        const newItems = items.filter(item => item.id !== itemId);
        const totals = calculateTotals(newItems);

        set({
          items: newItems,
          ...totals,
          updatedAt: new Date().toISOString(),
        });
      },

      updateQuantity: (itemId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        const { items } = get();
        const newItems = items.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        );
        const totals = calculateTotals(newItems);

        set({
          items: newItems,
          ...totals,
          updatedAt: new Date().toISOString(),
        });
      },

      clearCart: () => {
        set({
          items: [],
          subtotal: 0,
          tax: 0,
          deliveryFee: 0,
          total: 0,
          itemCount: 0,
          lastAddedItem: undefined,
          updatedAt: new Date().toISOString(),
        });
      },

      clearAddFeedback: () => set({ lastAddedItem: undefined }),

      setBranchId: (branchId) => set({ branchId }),

      setCustomerId: (customerId) => set({ customerId }),

      openCart: () => set({ isOpen: true }),

      closeCart: () => set({ isOpen: false }),

      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      setSyncing: (isSyncing) => set({ isSyncing }),

      setLastSynced: (lastSyncedAt) => set({ lastSyncedAt }),

      setError: (lastError) => set({ lastError }),

      loadPricing: async () => {
        await loadPricingConfig();
        const { items } = get();
        const totals = calculateTotals(items);
        set({ ...totals, pricingLoaded: true });
      },

      getItemCount: () => get().itemCount,

      getSubtotal: () => get().subtotal,

      getTotal: () => get().total,

      getItemByProduct: (productId, variantId) => {
        const itemId = generateItemId(productId, variantId);
        return get().items.find(item => item.id === itemId);
      },
    }),
    {
      name: 'karebe-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        branchId: state.branchId,
        customerId: state.customerId,
        subtotal: state.subtotal,
        tax: state.tax,
        deliveryFee: state.deliveryFee,
        total: state.total,
        itemCount: state.itemCount,
        createdAt: state.createdAt,
        updatedAt: state.updatedAt,
        lastSyncedAt: state.lastSyncedAt,
      }),
    }
  )
);

export const useCartItems = () => useCartStore((state) => state.items);
export const useCartTotal = () => useCartStore((state) => state.total);
export const useCartSubtotal = () => useCartStore((state) => state.subtotal);
export const useCartItemCount = () => useCartStore((state) => state.itemCount);
export const useCartIsOpen = () => useCartStore((state) => state.isOpen);
export const useCartBranchId = () => useCartStore((state) => state.branchId);
export const useCartIsSyncing = () => useCartStore((state) => state.isSyncing);
